import { useState, useEffect, useRef } from 'react';
import {
  getProfileData,
  getAnonData,
  saveProfileData,
  clearAllData,
  fullAccountReset,
  syncProfileToSupabase,
  syncAllConnectionsToSupabase,
} from '../../services/storage/userDataStorage';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSession } from '../../contexts/SessionContext';
import { COLORS, SHADOWS } from '../../styles/UISystem';
import { ButtonRounded } from '../../components/UISystem/Buttons';
import { X } from '@phosphor-icons/react/dist/ssr/X';
import { Check } from '@phosphor-icons/react/dist/ssr/Check';
import { Image } from '@phosphor-icons/react/dist/ssr/Image';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import IconBack from '../../components/UISystem/Icons/IconBack';
import { Plus } from '@phosphor-icons/react/dist/ssr/Plus';
import { IconCircleButton } from '../../components/UISystem/Icons/IconCircleButton';
import ProfileForm from '../../components/UISystem/Forms/ProfileForm';
import NameInput from '../../components/UISystem/Forms/NameInput';
import PhotoSelectionOverlay from '../../components/Overlay/PhotoSelectionOverlay';
import useProfilePhotoPicker from '../../hooks/useProfilePhotoPicker';
import { announceConversionGrant } from '../../services/sharing/conversionGrant';
import { getOrCreateProfileUserId, isValidProfileUserId } from '../../utils/identityUtils';
import { isPWA } from '../../utils/installPrompt';
import { ALL_CONTACT_KEYS } from '../../utils/contactKeys';
import { isSimpleTier as isSimpleTierNow, clearStoredTier } from '../../utils/userTier';
import { isDevToolsEnabled } from '../../utils/devTools';

// Demo data for quick testing (loaded via "Default info" button)
const DEMO_PROFILE_DATA = {
  name: 'Mark Mangan',
  phone: '+19176502256',
  email: 'markmangan@gmail.com',
  instagram: 'markesan',
  linkedin: 'markmangan',
  whatsapp: '+19176502256',
  photoUrl: '/images/profiles/mark.jpg',
  role: 'Founder',
  organization: 'Shoshin Labs',
  whatIDo: 'I build digital things for better living IRL',
  currentCity: 'NYC',
  placesLived: 'Philly, Vermont, Paris',
};

// Empty defaults for new users
const DEFAULT_PROFILE_DATA = {
  name: '',
  phone: '',
  email: '',
  instagram: '',
  linkedin: '',
  whatsapp: '',
  photoUrl: '',
  role: '',
  organization: '',
  whatIDo: '',
  currentCity: '',
  placesLived: '',
};

/**
 * Clean profile data before saving - removes empty contact fields
 * This prevents empty fields from persisting and showing when returning to the page
 */
const cleanProfileForSave = (profile) => {
  const cleaned = { ...profile };
  for (const key of ALL_CONTACT_KEYS) {
    if (key in cleaned && !cleaned[key]?.trim()) {
      delete cleaned[key];
    }
  }
  return cleaned;
};

/**
 * Profile Screen
 *
 * Two distinct layouts:
 * - Creation flow (first-time Profile + Anon→Profile): full-screen with X + Done button
 * - Edit mode (returning Profile user): floating card matching Detail pages
 *
 * Features:
 * - Photo selection from predefined images
 * - US phone formatting (xxx) yyy-zzzz
 * - Email validation
 * - Instagram with @ symbol
 * - Default test data for new Profile users
 */
const NAME_CONFIRMED_KEY = 'profile_name_confirmed';

// Dev-only chrome on this screen: Load default info / Clear data / custom ID,
// and the stock-photo sheet behind the avatar tap.
//
// Hidden from testers (user, 2026-08-20) but visible to whoever turned dev
// tools on for their own browser — the same `?dev=1` flag the People-header
// pills use. It replaces `REACT_APP_BRANCH !== 'main'`, which asked WHERE the
// app runs when the question is WHO is looking, and so showed a developer
// stock-photo sheet to real testers on every branch deploy.
//
// Read at module scope: the flag only changes via a ?dev= URL, which reloads.
const SHOW_DEV_TOOLS = isDevToolsEnabled();

const ProfileScreen = () => {
  const { goBack, navigateTo, screenParams } = useNavigation();
  const { sessionChatRoomId, setIsProfileUser } = useSession();

  const isProfileCreationMode = screenParams?.mode === 'anon-profile-creation';

  const [profile, setProfile] = useState({
    name: '',
    phone: '',
    email: '',
    instagram: '',
    whatsapp: '',
    linkedin: '',
    photoUrl: '',
    role: '',
    organization: '',
    whatIDo: '',
    currentCity: '',
    placesLived: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showPhotoSelector, setShowPhotoSelector] = useState(false);
  // Straight to the OS picker; the stock-photo sheet is dev-only, gated below
  // with the same expression as "Load default info" / "Clear data".
  const { openPhotoPicker, photoInput } = useProfilePhotoPicker((url) => handlePhotoSelect(url));
  const handlePhotoTap = () => (SHOW_DEV_TOOLS ? setShowPhotoSelector(true) : openPhotoPicker());
  const [isFirstTimeProfile, setIsFirstTimeProfile] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const scrollRef = useRef(null);
  const initialProfileRef = useRef(null);
  // Dev tools state - Custom URL Editor
  const [showEditProfileId, setShowEditProfileId] = useState(false);
  const [editProfileIdValue, setEditProfileIdValue] = useState('');
  const [editProfileIdError, setEditProfileIdError] = useState('');
  const [profileUserId, setProfileUserId] = useState('');

  // Tap-to-edit name state (edit mode only)
  const [isNameFocused, setIsNameFocused] = useState(false);
  // The name shows as an editable pill on the FIRST open of this page only, so
  // someone arriving with a partial name from the exchange ("Ella") is prompted
  // to complete it. Every subsequent open is plain tap-to-edit text (user,
  // 2026-08-19 / 2026-08-20; frames _1a vs _1b).
  //
  // Read once into state, THEN stamp the flag on mount: this visit keeps the
  // pill because state was captured before the effect ran, and the next open
  // reads true. No dependency on the user touching or blurring the field.
  const [nameConfirmed] = useState(() => localStorage.getItem(NAME_CONFIRMED_KEY) === 'true');

  useEffect(() => {
    localStorage.setItem(NAME_CONFIRMED_KEY, 'true');
  }, []);
  const [syncError, setSyncError] = useState(null);
  const nameInputRef = useRef(null);

  // Derived: true for both first-time Profile and Anon→Profile conversion
  const isCreationFlow = isProfileCreationMode || isFirstTimeProfile;

  // profile_user_id is created at save time (handleDone / handleFirstTimeProfileDone),
  // NOT on mount. Creating it early caused a bug: visiting this screen and going back
  // left a premature profile_user_id that broke message sender normalization for Anons.

  // Load profile data when component mounts
  useEffect(() => {
    const loadProfile = async () => {
      try {
        setIsLoading(true);

        // Creation mode: pre-fill from anonData, skip normal profile load
        if (isProfileCreationMode) {
          const anonData = await getAnonData(sessionChatRoomId);
          if (anonData) {
            console.log('[PROFILE_SCREEN] Pre-filling with anonData:', anonData);
            const prefilled = {
              ...DEFAULT_PROFILE_DATA,
              name: anonData.name || '',
              phone: anonData.phone || '',
              email: anonData.email || '',
              instagram: anonData.instagram || '',
              whatsapp: anonData.whatsapp || '',
              linkedin: anonData.linkedin || '',
              photoUrl: '', // Anon users don't have photos yet
            };
            setProfile(prefilled);
            initialProfileRef.current = JSON.stringify(prefilled);
          }
          setIsLoading(false);
          // Scroll to top after load
          setTimeout(() => scrollRef.current?.scrollTo(0, 0), 0);
          return;
        }

        // Load from global storage
        let profileData = await getProfileData();

        if (!profileData || !profileData.name) {
          // New Profile user - first-time creation
          console.log('[PROFILE_SCREEN] New Profile user, first-time creation');
          setIsFirstTimeProfile(true);
          profileData = { ...DEFAULT_PROFILE_DATA };

          // Pre-fill from pending profile conversion (if available)
          const prefillName = localStorage.getItem('onboarding_prefill_name');
          const prefillPhone = localStorage.getItem('onboarding_prefill_phone');
          if (prefillName) {
            profileData.name = prefillName;
          }
          if (prefillPhone) {
            profileData.phone = prefillPhone;
          }
          if (prefillName || prefillPhone) {
            console.log('[PROFILE_SCREEN] Pre-filling from conversion:', {
              prefillName,
              prefillPhone,
            });
          }
        }

        if (profileData) {
          // Clean empty contact fields on load (UI only - storage cleaned on next save)
          const cleaned = cleanProfileForSave(profileData);
          setProfile(cleaned);
          initialProfileRef.current = JSON.stringify(cleaned);
        }

        setIsLoading(false);
        // Scroll to top after load
        setTimeout(() => scrollRef.current?.scrollTo(0, 0), 0);
      } catch (error) {
        console.error('[PROFILE_SCREEN] Error loading profile:', error);
        setIsLoading(false);
      }
    };

    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load profileUserId from localStorage for Custom URL Editor (dev tool)
  useEffect(() => {
    const storedProfileUserId = localStorage.getItem('profile_user_id');
    if (storedProfileUserId) {
      setProfileUserId(storedProfileUserId);
    }
  }, []);

  // Handle profile changes from form
  const handleProfileChange = (updatedProfile) => {
    setProfile(updatedProfile);
    if (syncError) setSyncError(null);
  };

  // Sync the RAW form state — the one home for the raw-vs-cleaned contract:
  // a field cleared this session is '' (an honored clear); a never-present field
  // is absent (preserved by the sync's merge). localStorage still gets the
  // CLEANED object at each save site — the spec'd empty-field cleanup is
  // unchanged. Passing cleanedProfile here would silently break DB clears.
  const syncRawProfile = (profileUserId) => syncProfileToSupabase({ ...profile, profileUserId });

  // Handle photo selection — from the OS picker, or the dev stock sheet
  const handlePhotoSelect = (photoUrl) => {
    setProfile((prev) => ({ ...prev, photoUrl }));
    setShowPhotoSelector(false);
  };

  // Load demo data for quick testing — preserve verified phone
  const handleLoadDemoData = () => {
    const phoneVerified = localStorage.getItem('phone_verified') === 'true';
    setProfile((prev) => ({
      ...DEMO_PROFILE_DATA,
      ...(phoneVerified && prev.phone ? { phone: prev.phone } : {}),
    }));
  };

  // Clear all profile data
  const handleClearData = () => {
    setProfile({ ...DEFAULT_PROFILE_DATA });
  };

  // Validation: name + photo + phone (phone is required for PWA OTP auth).
  // Simple tier (tiered-exchange Step 5, 6d): name + ≥1 contact — no phone or
  // photo requirement; an SP has no OTP to feed and photo is optional polish.
  const isSimpleTier = isSimpleTierNow();
  const hasAnyContact = ALL_CONTACT_KEYS.some((k) =>
    typeof profile[k] === 'string' ? profile[k].trim() : profile[k]
  );
  const isProfileValid = isSimpleTier
    ? Boolean(profile.name?.trim() && hasAnyContact)
    : Boolean(profile.name?.trim() && profile.photoUrl && profile.phone?.trim());

  // QR-socials onboarding variant (mock 6d, user ruling 2026-08-08): reached
  // from the QR's "Add your socials" — no Back/Save header, no reset/dev
  // chrome; ONE pinned Done that always continues the flow back to the QR.
  const fromQrSocials = isSimpleTier && screenParams?.source === 'qr-socials';

  // Handle Done button click (first-time Profile user creation)
  const handleFirstTimeProfileDone = async () => {
    if (!isProfileValid) return;

    try {
      console.log('[PROFILE_SCREEN] First-time Profile user - saving and navigating to QR');
      // Ensure profile_user_id exists (creates if missing, e.g., user skipped photo selection)
      const currentProfileUserId = getOrCreateProfileUserId();
      const cleanedProfile = {
        ...cleanProfileForSave(profile),
        profileUserId: currentProfileUserId,
      };

      // Validate + sync to Supabase FIRST — blocks on phone uniqueness before local save.
      const syncResult = await syncRawProfile(currentProfileUserId);
      if (syncResult?.error === 'phone_taken') {
        setSyncError('This phone number is already in use');
        setIsSaving(false);
        return;
      }
      if (!syncResult) {
        console.warn('[PROFILE_SCREEN] Supabase sync failed, but continuing');
      }

      await saveProfileData(cleanedProfile);
      console.log('[PROFILE_SCREEN] Profile data saved');

      // Check if this was a pending profile conversion (user already verified via OTP)
      const wasConversion = localStorage.getItem('needs_onboarding') === 'true';

      // Clear onboarding flags (set by pending profile conversion)
      localStorage.removeItem('needs_onboarding');
      localStorage.removeItem('onboarding_prefill_name');
      localStorage.removeItem('onboarding_prefill_phone');

      // OTP-first conversion: the Conversion Grant — announce to ALL existing
      // connections (full name + hometown; see services/sharing/conversionGrant).
      // The peer's profile fetch at detection time races this form (it fired
      // before the hometown was typed) — the announcement is the reliable carrier.
      if (wasConversion) {
        await announceConversionGrant(sessionChatRoomId, cleanedProfile);
      }

      // Set user type and navigate
      sessionStorage.setItem('user_type', 'profile');
      // Conversion users and PWA users go straight to list — they don't need install instructions
      navigateTo(isPWA() || wasConversion ? 'profile-connections-list' : 'profile-signup-success');
    } catch (error) {
      console.error('[PROFILE_SCREEN] Error saving first-time profile:', error);
    }
  };

  // Handle Done button click (profile-creation mode — Anon → Profile conversion)
  const handleDone = async () => {
    if (!isProfileValid || isSaving) return;

    try {
      setIsSaving(true);

      // Save as global profile data (clean empty contact fields)
      // Ensure profile_user_id exists (creates if missing, e.g., user skipped photo selection)
      const currentProfileUserId = getOrCreateProfileUserId();
      const cleanedProfile = {
        ...cleanProfileForSave(profile),
        profileUserId: currentProfileUserId,
      };

      // Validate + sync to Supabase FIRST — blocks on phone uniqueness before local save.
      const syncResult = await syncRawProfile(currentProfileUserId);
      if (syncResult?.error === 'phone_taken') {
        setSyncError('This phone number is already in use');
        setIsSaving(false);
        return;
      }
      if (!syncResult) {
        console.warn('[PROFILE_SCREEN] Supabase sync failed, but continuing');
      }

      await saveProfileData(cleanedProfile);
      console.log('[PROFILE_SCREEN] Saved profile data');

      // The Conversion Grant — announce to ALL existing connections (full name
      // + hometown; shared helper, same announcement in both conversion flows)
      await announceConversionGrant(sessionChatRoomId, cleanedProfile);

      // Set user type to profile
      setIsProfileUser(true);
      sessionStorage.setItem('user_type', 'profile');
      console.log('[PROFILE_SCREEN] User type set to profile');

      // Post-conversion: sync all connection data to Supabase (blocking — must complete before
      // navigating to install screen, which reloads the page and would kill an in-flight sync)
      const profileUserId = localStorage.getItem('profile_user_id');
      if (profileUserId) {
        localStorage.setItem('pending_initial_sync', 'true');
        await syncAllConnectionsToSupabase(profileUserId);
      }

      // PWA users skip install screen — they already have the app
      navigateTo(isPWA() ? 'profile-connections-list' : 'profile-signup-success');
    } catch (error) {
      console.error('[PROFILE_SCREEN] Error in handleDone:', error);
      setIsSaving(false);
    }
  };

  // Shared save body (edit-mode checkmark + the 6d Done): Supabase sync FIRST
  // (blocks on phone uniqueness), then local save. Returns false on a blocking
  // validation error (phone taken).
  const saveEditedProfile = async () => {
    const currentProfileUserId = localStorage.getItem('profile_user_id');
    const cleanedProfile = {
      ...cleanProfileForSave(profile),
      profileUserId: currentProfileUserId,
    };
    const syncResult = await syncRawProfile(currentProfileUserId);
    if (syncResult?.error === 'phone_taken') {
      setSyncError('This phone number is already in use');
      setIsSaving(false);
      return false;
    }
    if (!syncResult) {
      console.warn('[PROFILE_SCREEN] Supabase sync failed, but continuing');
    }
    await saveProfileData(cleanedProfile);
    console.log('[PROFILE_SCREEN] Saved Profile data');
    return true;
  };

  // Save profile and navigate back (edit mode checkmark)
  const handleSave = async () => {
    if (!isProfileValid) return;

    try {
      if (!(await saveEditedProfile())) return;
      // Navigate back (default to connections list for profile users)
      goBack('profile-connections-list');
    } catch (error) {
      console.error('[PROFILE_SCREEN] Error saving profile:', error);
      // Still navigate back even if save fails
      goBack('profile-connections-list');
    }
  };

  // 6d Done — the onboarding continue: save when there's a valid change, then
  // land on PEOPLE (mock 7d "Back from Flow" — the loop completes on their
  // home base; the 7c "Swap contacts" QR is what Connect opens later). The
  // flow never blocks here; the one hard stop is a phone-taken conflict
  // (inline error).
  const handleQrSocialsDone = async () => {
    try {
      if (isProfileDirty() && isProfileValid) {
        if (!(await saveEditedProfile())) return;
      }
    } catch (error) {
      console.error('[PROFILE_SCREEN] QR-socials save failed — continuing:', error);
    }
    navigateTo('profile-connections-list', {}, true);
  };

  // Full reset — confirm, wipe (account-level when an id exists), reload
  // One delete affordance for every own-profile state (user ruling 2026-08-19:
  // "It should probably always be there. Someone creates a QR, sees they now
  // have a profile, and freak out"). Two words and nothing else — the
  // confirmation carries the scope ("all your data including connections"), so
  // a caption above it would say the same thing twice.
  //
  // "Profile", not "Account": account implies credentials and a login, which a
  // Simple-tier or pre-verification user doesn't have — but everyone here has a
  // profile, and it's the word the product uses everywhere else.
  //
  // Replaces two tier-split treatments — a grey in-flow "Reset app" box for
  // Full, a raised pill for Simple — neither of which appeared during creation,
  // which is exactly when someone might want out.
  // Pushed to the BOTTOM of the page, never sitting right under the content
  // (user, 2026-08-20): trailing it directly after the last field makes the most
  // destructive control on the screen read as the next feature in the stack.
  // marginTop:auto claims the leftover space in the scroll column, so it lands
  // at the bottom on a short page and simply follows the content on a long one.
  const renderDeleteProfile = () => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '6px',
        padding: '24px 0',
        marginTop: 'auto',
      }}
    >
      <div
        onClick={handleResetApp}
        style={{
          fontSize: '14px',
          color: '#8E8E93',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        Delete Profile
      </div>
    </div>
  );

  const handleResetApp = async () => {
    if (window.confirm('Are you sure? This will delete all your data including connections.')) {
      const currentId = localStorage.getItem('profile_user_id');
      if (currentId) {
        await fullAccountReset(currentId);
      } else {
        await clearAllData();
      }
      window.location.href = '/';
    }
  };

  // Check if profile has been modified since load
  const isProfileDirty = () => {
    if (!initialProfileRef.current) return false;
    return JSON.stringify(profile) !== initialProfileRef.current;
  };

  // Cancel creation — confirm if dirty, then discard and go back
  const handleCancel = () => {
    if (isProfileDirty()) {
      setShowExitConfirm(true);
      return;
    }
    confirmCancel();
  };

  // Actually leave (after confirmation or if clean)
  const confirmCancel = () => {
    setShowExitConfirm(false);
    if (isProfileCreationMode) {
      goBack(); // Anon conversion — back to anon flow
    } else {
      navigateTo('signup'); // First-time profile — back to signup
    }
  };

  // Cancel edit — confirm if dirty, then discard changes
  const handleEditCancel = () => {
    if (isProfileDirty()) {
      setShowExitConfirm(true);
      return;
    }
    goBack('profile-connections-list');
  };

  // Actually leave edit (after confirmation or if clean)
  const confirmEditCancel = () => {
    setShowExitConfirm(false);
    goBack('profile-connections-list');
  };

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: COLORS.background,
        }}
      >
        Loading...
      </div>
    );
  }

  // ===== CREATION FLOW — full-screen layout (first-time Profile + Anon→Profile) =====
  if (isCreationFlow) {
    return (
      <div
        style={{
          position: 'relative',
          height: '100vh',
          width: '100%',
          backgroundColor: COLORS.background,
          overflow: 'hidden',
        }}
      >
        {/* Safe area cover — white bar behind the dynamic island */}
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            height: 'env(safe-area-inset-top)',
            backgroundColor: '#FFFFFF',
            zIndex: 4,
          }}
        />

        {/* Left halo — X button */}
        <div
          style={{
            position: 'fixed',
            top: 'env(safe-area-inset-top)',
            left: 0,
            width: '100px',
            height: '100px',
            background:
              'radial-gradient(circle at 45px 45px, #FFFFFF 0%, #FFFFFF 40%, rgba(255, 255, 255, 0) 70%)',
            pointerEvents: 'none',
            zIndex: 5,
          }}
        />

        {/* X button — fixed top-left */}
        <div
          onClick={handleCancel}
          style={{
            position: 'fixed',
            top: 'calc(16px + env(safe-area-inset-top))',
            left: '16px',
            width: '44px',
            height: '44px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#FFFFFF',
            boxShadow: SHADOWS.TIER2,
            cursor: 'pointer',
            zIndex: 10,
          }}
        >
          <X size={22} weight="regular" color="#555555" />
        </div>

        {/* Scrollable Content */}
        <div
          ref={scrollRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollPaddingTop: '16px',
          }}
        >
          {/* Photo - centered at top of scroll area */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              paddingTop: '76px',
            }}
          >
            <div
              onClick={handlePhotoTap}
              style={{
                position: 'relative',
                width: '90px',
                height: '90px',
                borderRadius: '50%',
                backgroundColor: profile.photoUrl ? 'transparent' : '#1C1C1E',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundImage: profile.photoUrl ? `url(${profile.photoUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {!profile.photoUrl && <Image size={28} weight="regular" color="#FFFFFF" />}
              {/* + badge */}
              {!profile.photoUrl && (
                <div style={{ position: 'absolute', bottom: '-4px', right: '-4px' }}>
                  <IconCircleButton
                    icon={Plus}
                    size={16}
                    buttonSize={32}
                    iconColor="#1C1C1E"
                    iconWeight="bold"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Title */}
          <div
            style={{
              textAlign: 'center',
              padding: '16px 20px 0',
            }}
          >
            <div
              style={{
                fontSize: '22px',
                fontWeight: '600',
                color: '#1C1C1E',
                marginBottom: '8px',
              }}
            >
              My Profile
            </div>
          </div>

          <ProfileForm
            profile={profile}
            onProfileChange={handleProfileChange}
            hidePhoto={true}
            isCreationFlow={true}
          />

          {syncError && (
            <div style={{ padding: '0 20px', maxWidth: '600px', margin: '0 auto' }}>
              <div
                style={{
                  color: '#FF3B30',
                  fontSize: '14px',
                  fontWeight: '500',
                  textAlign: 'center',
                }}
              >
                {syncError}
              </div>
            </div>
          )}

          {/* Done button — inline below form */}
          <div style={{ padding: '8px 20px 0', maxWidth: '600px', margin: '0 auto' }}>
            <ButtonRounded
              text={isSaving ? 'Saving...' : 'Done'}
              size="full"
              onClick={isProfileCreationMode ? handleDone : handleFirstTimeProfileDone}
              disabled={!isProfileValid || isSaving}
            />
            {isProfileValid && !isSaving && (
              <div
                style={{
                  textAlign: 'center',
                  fontSize: '13px',
                  color: '#8E8E93',
                  marginTop: '10px',
                }}
              >
                You always decide what to share
              </div>
            )}
          </div>

          {/* Dev tools, pushed below the fold. Was on the raw branch check while
              its two siblings used SHOW_DEV_TOOLS, so these two buttons kept
              rendering for testers after the 2026-08-20 hide request. */}
          {SHOW_DEV_TOOLS && (
            <div
              style={{
                textAlign: 'center',
                padding: '80px 20px 0',
              }}
            >
              <button
                onClick={handleLoadDemoData}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#D1D1D6',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Load default info
              </button>
              <span style={{ margin: '0 12px', color: '#E5E5EA' }}>|</span>
              <button
                onClick={handleClearData}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#D1D1D6',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                Clear data
              </button>
            </div>
          )}
        </div>

        {/* Photo Selection Overlay */}
        {photoInput}
        {showPhotoSelector && (
          <PhotoSelectionOverlay
            currentPhotoUrl={profile.photoUrl}
            onSelect={handlePhotoSelect}
            onClose={() => setShowPhotoSelector(false)}
          />
        )}

        {/* Exit confirmation overlay */}
        {showExitConfirm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
            onClick={() => setShowExitConfirm(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '20px',
                width: '85%',
                maxWidth: '350px',
                boxShadow: SHADOWS.TIER3_UP,
                textAlign: 'center',
              }}
            >
              <h3 style={{ margin: '0 0 8px', color: '#000', fontWeight: 700, fontSize: '18px' }}>
                Discard changes?
              </h3>
              <p style={{ margin: '0 0 24px', color: '#666', fontSize: '15px' }}>
                All edits will be lost
              </p>
              <ButtonRounded text="Yes" size="full" onClick={confirmCancel} />
              <ButtonRounded
                text="Keep editing"
                variant="blank"
                size="full"
                onClick={() => setShowExitConfirm(false)}
                style={{ marginTop: '4px' }}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // ===== EDIT MODE — floating card layout matching Detail pages =====
  const dirty = isProfileDirty();
  const showSaveButton = dirty && isProfileValid;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '100dvh',
        backgroundColor: 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px 16px',
      }}
    >
      {/* White card container */}
      <div
        style={{
          position: 'relative',
          backgroundColor: '#FFFFFF',
          borderRadius: '32px',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Thin top edge fade */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '20px',
            background: 'linear-gradient(to bottom, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)',
            pointerEvents: 'none',
            zIndex: 5,
            borderTopLeftRadius: '32px',
            borderTopRightRadius: '32px',
          }}
        />

        {/* Left halo + Back/close button (CaretLeft clean, X dirty) — hidden
            on the QR-socials onboarding variant: Done is the only exit (6d) */}
        {!fromQrSocials && (
          <>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100px',
                height: '100px',
                background:
                  'radial-gradient(circle at 45px 45px, #FFFFFF 0%, #FFFFFF 40%, rgba(255, 255, 255, 0) 70%)',
                pointerEvents: 'none',
                zIndex: 5,
                borderTopLeftRadius: '32px',
              }}
            />
            <div style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 10 }}>
              <IconBack
                icon={dirty ? X : undefined}
                onClick={dirty ? handleEditCancel : () => goBack('profile-connections-list')}
              />
            </div>
          </>
        )}

        {/* Right halo + checkmark save button — only when dirty AND valid */}
        {showSaveButton && !fromQrSocials && (
          <>
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '100px',
                height: '100px',
                background:
                  'radial-gradient(circle at calc(100% - 45px) 45px, #FFFFFF 0%, #FFFFFF 40%, rgba(255, 255, 255, 0) 70%)',
                pointerEvents: 'none',
                zIndex: 5,
                borderTopRightRadius: '32px',
              }}
            />
            <div
              onClick={handleSave}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                width: '44px',
                height: '44px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#1C1C1E',
                boxShadow: SHADOWS.TIER2,
                cursor: 'pointer',
                zIndex: 10,
              }}
            >
              <Check size={22} weight="bold" color="#FFFFFF" />
            </div>
          </>
        )}

        {/* Scrollable content area */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            WebkitOverflowScrolling: 'touch',
            // Column with a full-height floor so Delete Profile's marginTop:auto
            // has space to claim — it sits at the bottom of a short page and
            // simply follows the content once the page scrolls.
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              minHeight: '100%',
            }}
          >
            {/* Profile Content Block */}
            <div
              style={{
                paddingTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              {/* Photo — tap to change. THIS is the photo the own-profile page
                renders; ProfileForm's own photo block never mounts here
                (hidePhoto), so the frames' + badge belongs on this element.
                Silhouette + dark badge while empty, both gone once a photo
                exists (own-profile_1a vs _1b). */}
              <div onClick={handlePhotoTap} style={{ position: 'relative', cursor: 'pointer' }}>
                <div
                  style={{
                    width: '90px',
                    height: '90px',
                    borderRadius: '50%',
                    backgroundColor: profile.photoUrl ? 'transparent' : '#F2F2F7',
                    backgroundImage: profile.photoUrl ? `url(${profile.photoUrl})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {!profile.photoUrl && <User size={48} weight="fill" color="#C7C7CC" />}
                </div>
                {!profile.photoUrl && (
                  <div
                    style={{
                      position: 'absolute',
                      right: '0px',
                      bottom: '4px',
                      width: '28px',
                      height: '28px',
                      borderRadius: '50%',
                      backgroundColor: '#000000',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Plus size={16} weight="bold" color="#FFFFFF" />
                  </div>
                )}
              </div>

              {/* Name — tap-to-edit inline */}
              {isNameFocused || !nameConfirmed ? (
                <NameInput
                  ref={nameInputRef}
                  value={profile.name}
                  onChange={(e) => handleProfileChange({ ...profile, name: e.target.value })}
                  onBlur={() => setIsNameFocused(false)}
                  placeholder="Your name"
                />
              ) : (
                <div
                  onClick={() => {
                    setIsNameFocused(true);
                    requestAnimationFrame(() => nameInputRef.current?.focus());
                  }}
                  style={{
                    fontSize: '24px',
                    fontWeight: profile.name ? '700' : '500',
                    color: profile.name ? '#000000' : '#8E8E93',
                    letterSpacing: '-0.3px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    padding: '8px 16px',
                    borderRadius: '16px',
                    transition: 'background 0.2s ease',
                  }}
                >
                  {profile.name || 'Your name'}
                </div>
              )}

              {/* The sharing line moved into ProfileForm, where it renders for
                every tier and every state rather than Simple tier only — it was
                also carrying copy from a temporary test build. */}
            </div>

            {/* Profile Form — contacts, hometown and About me. One treatment for
              every tier since 2026-08-20: the Simple-tier "contacts only"
              minimum is retired, About me is collapsed rather than absent. */}
            <ProfileForm
              profile={profile}
              onProfileChange={handleProfileChange}
              hidePhoto={true}
              hideName={true}
              isCreationFlow={false}
            />

            {syncError && (
              <div style={{ padding: '8px 20px', textAlign: 'center' }}>
                <div style={{ color: '#FF3B30', fontSize: '14px', fontWeight: '500' }}>
                  {syncError}
                </div>
              </div>
            )}

            {/* Dev tools - only show on non-main branches (never on the 6d
              onboarding variant — it's a clean product surface) */}
            {/* Hidden for now (user, 2026-08-20) — re-enable for dev testing
              by flipping SHOW_DEV_TOOLS back to the branch check. */}
            {SHOW_DEV_TOOLS && !fromQrSocials && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '8px 20px 0',
                }}
              >
                <button
                  onClick={handleLoadDemoData}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#8E8E93',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Load default info
                </button>
                <span style={{ margin: '0 12px', color: '#D1D1D6' }}>|</span>
                <button
                  onClick={handleClearData}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#8E8E93',
                    fontSize: '14px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Clear data
                </button>
              </div>
            )}

            {/* Delete — one treatment for every state (see renderDeleteProfile) */}
            {!fromQrSocials && renderDeleteProfile()}
          </div>

          {/* Custom ID — dev-only, hidden with the rest of the dev tools
              (user, 2026-08-20). Conditionally rendered, never display:none —
              review 2026-08-09: the toggle left an invisible live onClick. */}
          {SHOW_DEV_TOOLS && !fromQrSocials && (
            <div
              onClick={() => {
                setEditProfileIdValue(profileUserId || '');
                setShowEditProfileId(true);
              }}
              style={{
                textAlign: 'center',
                paddingBottom: '16px',
                cursor: 'pointer',
              }}
            >
              <span
                style={{
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  color: '#C7C7CC',
                }}
              >
                {profileUserId || '\u00A0'}
              </span>
            </div>
          )}

          {/* Bottom padding for scroll — taller when a pinned pill overlays */}
          <div style={{ height: isSimpleTier ? '120px' : '40px', flexShrink: 0 }} />
        </div>
        {/* End scrollable content area */}

        {/* 6d Done — the onboarding variant's single pinned action */}
        {fromQrSocials && (
          <div
            style={{
              position: 'absolute',
              bottom: 'calc(24px + env(safe-area-inset-bottom))',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
            }}
          >
            <ButtonRounded
              text="Done"
              variant="primary"
              size="auto"
              style={{ minWidth: '180px' }}
              onClick={handleQrSocialsDone}
            />
          </div>
        )}
      </div>

      {/* Photo Selection Overlay — outside card */}
      {photoInput}
      {showPhotoSelector && (
        <PhotoSelectionOverlay
          currentPhotoUrl={profile.photoUrl}
          onSelect={handlePhotoSelect}
          onClose={() => setShowPhotoSelector(false)}
        />
      )}

      {/* Edit Profile ID Modal — outside card */}
      {showEditProfileId && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
        >
          <div
            style={{
              backgroundColor: '#FFFFFF',
              borderRadius: '16px',
              padding: '24px',
              width: '85%',
              maxWidth: '360px',
              boxShadow: SHADOWS.TIER4,
            }}
          >
            <h3
              style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#1C1C1E',
                marginTop: 0,
                marginBottom: '8px',
              }}
            >
              Edit Profile ID
            </h3>
            <p
              style={{
                fontSize: '14px',
                color: '#8E8E93',
                marginBottom: '16px',
              }}
            >
              When creating custom QRs
            </p>
            <input
              type="text"
              value={editProfileIdValue}
              onChange={(e) => {
                setEditProfileIdValue(e.target.value);
                setEditProfileIdError('');
              }}
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                fontFamily: 'monospace',
                border: `1px solid ${editProfileIdError ? '#FF3B30' : '#E5E5E5'}`,
                borderRadius: '10px',
                marginBottom: '4px',
                boxSizing: 'border-box',
              }}
            />
            {editProfileIdError && (
              <p
                style={{
                  fontSize: '12px',
                  color: '#FF3B30',
                  margin: '0 0 4px 0',
                }}
              >
                {editProfileIdError}
              </p>
            )}
            <p
              style={{
                fontSize: '12px',
                color: '#8E8E93',
                marginBottom: '20px',
                wordBreak: 'break-all',
              }}
            >
              /u/{editProfileIdValue || '...'}
            </p>
            <div
              style={{
                display: 'flex',
                gap: '12px',
              }}
            >
              <ButtonRounded
                text="Cancel"
                variant="secondary"
                size="auto"
                onClick={() => setShowEditProfileId(false)}
                style={{ flex: 1 }}
              />
              <ButtonRounded
                text="Save"
                variant="blue"
                size="auto"
                onClick={() => {
                  const trimmed = editProfileIdValue.trim();
                  if (!trimmed) return;
                  if (!isValidProfileUserId(trimmed)) {
                    setEditProfileIdError('4-12 chars, letters/numbers/dash/underscore only');
                    return;
                  }
                  localStorage.setItem('profile_user_id', trimmed);
                  clearStoredTier(); // a different profile: reseed its tier on open
                  setShowEditProfileId(false);
                  window.location.reload();
                }}
                style={{ flex: 1 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Exit confirmation overlay — outside card */}
      {showExitConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowExitConfirm(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '20px',
              width: '85%',
              maxWidth: '350px',
              boxShadow: SHADOWS.TIER3_UP,
              textAlign: 'center',
            }}
          >
            <h3 style={{ margin: '0 0 8px', color: '#000', fontWeight: 700, fontSize: '18px' }}>
              Discard changes?
            </h3>
            <p style={{ margin: '0 0 24px', color: '#666', fontSize: '15px' }}>
              All edits will be lost
            </p>
            <ButtonRounded text="Yes" size="full" onClick={confirmEditCancel} />
            <ButtonRounded
              text="Keep editing"
              variant="blank"
              size="full"
              onClick={() => setShowExitConfirm(false)}
              style={{ marginTop: '4px' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileScreen;
